import {useCallback, useEffect, useRef, useState} from 'react';
import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {minutesToWait} from './missingAccountModel';

/**
 * «Ya está: buscar cuentas nuevas» (cc#209). Le pide al backend que descubra YA las cuentas del canal
 * (`POST agency/channels/<CANAL>/account-discovery`) y consulta cada 5 s hasta que termina; al
 * terminar avisa (`onFinished`) para que el modal vuelva a leer las cuentas. Antes, quien asignaba
 * la cuenta en la plataforma esperaba la revisión periódica: 3 horas de día, 6 de noche.
 *
 * Estados: `idle` · `running` · `done` · `rate_limited` (con `minutes`) · `error`.
 * La consulta se pausa con la pestaña oculta y se rinde a los 3 minutos (relee las cuentas igual).
 */

export const DISCOVERY_POLL_INTERVAL_MS = 5000;
export const DISCOVERY_MAX_DURATION_MS = 3 * 60 * 1000;

const RATE_LIMITED = 'DISCOVERY_RATE_LIMITED';

export function useAccountDiscovery(channel, onFinished) {
    const [state, setState] = useState({kind: 'idle'});
    const aliveRef = useRef(true);
    const timerRef = useRef(null);
    const startedAtRef = useRef(null);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const url = `${API_URL}agency/channels/${encodeURIComponent(channel)}/account-discovery`;

    const stop = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            stop();
        };
    }, [stop]);

    const finish = useCallback(() => {
        stop();
        if (!aliveRef.current) return;
        setState({kind: 'done'});
        if (onFinishedRef.current) onFinishedRef.current();
    }, [stop]);

    const pollRef = useRef(null);
    const schedule = useCallback(() => {
        stop();
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (aliveRef.current && pollRef.current) pollRef.current();
        }, DISCOVERY_POLL_INTERVAL_MS);
    }, [stop]);

    const poll = useCallback(() => {
        if (Date.now() - startedAtRef.current > DISCOVERY_MAX_DURATION_MS) {
            finish();
            return;
        }
        // Con la pestaña oculta no se pregunta: quien fue a la plataforma a asignar la cuenta vuelve
        // y la consulta sigue desde ahí.
        if (typeof document !== 'undefined' && document.hidden) {
            schedule();
            return;
        }
        new APIClient(url).call('GET').then((response) => {
            if (!aliveRef.current) return;
            if (isOk(response) && response.data && response.data.running === false) {
                finish();
                return;
            }
            schedule();
        }).catch(() => aliveRef.current && schedule());
    }, [url, finish, schedule]);
    pollRef.current = poll;

    const start = useCallback(() => {
        if (state.kind === 'running') return;
        setState({kind: 'running'});
        new APIClient(url).call('POST').then((response) => {
            if (!aliveRef.current) return;
            if (isOk(response)) {
                startedAtRef.current = Date.now();
                schedule();
                return;
            }
            if (response && response.status === RATE_LIMITED) {
                const seconds = response.raw_body && response.raw_body.retry_after_seconds;
                setState({kind: 'rate_limited', minutes: minutesToWait(seconds)});
                return;
            }
            setState({kind: 'error'});
        }).catch(() => aliveRef.current && setState({kind: 'error'}));
    }, [url, schedule, state.kind]);

    return {state, start};
}
